import axios from "axios";

export const PIZZERIA_URL = process.env.REACT_APP_PIZZERIA_API_URL;
export const papi = axios.create({ baseURL: `${PIZZERIA_URL}/api` });

const TOKEN_KEY = "p400_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
papi.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const STORE_NAME = "Pizzeria 400° Gradi";
export const SIZE_LABELS = { rotonda: "Rotonda", mezza: "Mezza teglia", teglia: "Teglia" };
export const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

export const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export function formatError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.response ? "Qualcosa è andato storto. Riprova." : "Errore di connessione. Controlla la rete e riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d))).join(" ");
  return typeof detail.msg === "string" ? detail.msg : String(detail);
}

export const fileUrl = (u) => (!u ? null : u.startsWith("/api/") ? `${PIZZERIA_URL}${u}` : u);

export const sizesOf = (p) =>
  p.sized ? ["rotonda", "teglia", "mezza"].filter((s) => p[`price_${s}`] != null) : [];

export const basePrice = (p, size) => (size ? p[`price_${size}`] : p.price) || 0;

const romeNow = () => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, weekday: "short" }).formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}`, day: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(parts.weekday) };
};

export const isPromoToday = (pr) => {
  const n = romeNow();
  if (pr.active === false) return false;
  if (pr.starts_on && n.date < pr.starts_on) return false;
  if (pr.ends_on && n.date > pr.ends_on) return false;
  return !pr.days?.length || pr.days.includes(n.day);
};

export const promoWhen = (pr) => {
  const days = pr.days?.length && pr.days.length < 7 ? pr.days.map((d) => DAYS[d]?.slice(0, 3)).join(", ") : "";
  const hours = pr.from_time || pr.to_time ? `dalle ${pr.from_time || "apertura"} alle ${pr.to_time || "chiusura"}` : "";
  const until = pr.ends_on ? `fino al ${new Date(pr.ends_on).toLocaleDateString("it-IT")}` : "";
  return [days, hours, until].filter(Boolean).join(" · ");
};

export const promoOf = (p, deals) => {
  const f = deals?.featured;
  const fromFeatured = f && f.product_id === p.id ? f.discount_percent || 0 : 0;
  const fromPromos = (deals?.promos || []).filter((pr) => pr.kind === "products" && pr.product_ids?.includes(p.id) && isPromoToday(pr)).map((pr) => pr.percent);
  return Math.max(fromFeatured, ...fromPromos, 0);
};

export const priceFor = (p, size, featured) => {
  const pct = promoOf(p, featured);
  const b = basePrice(p, size);
  return pct ? Math.round(b * (100 - pct)) / 100 : b;
};

export const minPrice = (p, featured) => {
  const prices = sizesOf(p).map((s) => priceFor(p, s, featured));
  return prices.length ? Math.min(...prices) : priceFor(p, null, featured);
};

export const ingredientsOf = (p) =>
  (p.description || "").split(/,| e /).map((s) => s.trim()).filter((s) => s.length > 1 && s.length < 40);

const ORDERS_KEY = "p400_orders";
export const savedOrderIds = () => JSON.parse(localStorage.getItem(ORDERS_KEY) || "[]");
export const saveOrderId = (id) => localStorage.setItem(ORDERS_KEY, JSON.stringify([id, ...savedOrderIds().filter((x) => x !== id)].slice(0, 30)));

export const ORDER_STATUS = {
  nuovo: ["Ricevuto", "var(--brand-3)", "var(--on-brand-3)"],
  pending: ["Ricevuto", "var(--brand-3)", "var(--on-brand-3)"],
  in_preparazione: ["In forno", "#FEF3C7", "#92400E"],
  pronto: ["Pronto", "#DCFCE7", "#166534"],
  ritirato: ["Ritirato", "var(--surface-3)", "var(--muted-c)"],
  annullato: ["Annullato", "var(--surface-3)", "var(--muted-c)"],
};

export const statusInfo = (s) => ORDER_STATUS[s] || [s || "In elaborazione", "var(--surface-3)", "var(--muted-c)"];
export const isPast = (o) => ["ritirato", "annullato"].includes(o.status);

export const timeLabel = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return iso || "";
  const tz = { timeZone: "Europe/Rome" };
  const day = (x) => x.toLocaleDateString("it-IT", tz);
  const hm = d.toLocaleTimeString("it-IT", { ...tz, hour: "2-digit", minute: "2-digit" });
  if (day(d) === day(new Date())) return `oggi alle ${hm}`;
  if (day(d) === day(new Date(Date.now() + 86400000))) return `domani alle ${hm}`;
  return `${d.toLocaleDateString("it-IT", { ...tz, weekday: "long", day: "numeric", month: "long" })} alle ${hm}`;
};

export const lineMeta = (x) => [
  x.size && SIZE_LABELS[x.size],
  x.ingredients,
  x.extra_names?.length && `+ ${x.extra_names.join(", ")}`,
  !x.ingredients && x.removed?.length && `SENZA: ${x.removed.join(", ")}`,
].filter(Boolean).join(" · ");
